import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import { FlowNode, flowNodeColor, NodeTypeComponent } from 'fable/flowEditor';
import type { InternalPortT, NodeT, RegisterPortT } from 'fable/flowEditor';

import OpeningHoursFromPayload from '../../components/openingHoursFromPayload';
import { createOpeningHoursModalContainer } from '../../containers/modals/openingHours';
import { formatPortsHelper } from '../../helpers/nodePortFormatter';
import { FlowNodeTypes } from '../types';

export type OpeningHoursNodeIntervalsT = {
  open: string;
  close: string;
};

export type OpeningHoursNodeScheduleT = {
  date: string;
  intervals: OpeningHoursNodeIntervalsT[];
};

type FlowNodeOpeningHoursPayloadT = {
  openNodeId: string;
  closedNodeId: string;
  schedule: OpeningHoursNodeScheduleT[];
  timezone: string | null;
};

export type FlowNodeOpeningHoursT = NodeT<
  FlowNodeTypes.OPENING_HOURS,
  FlowNodeOpeningHoursPayloadT
>;

const portTranslator = (node: NodeT): RegisterPortT[] => [
  {
    id: uuid(),
    type: 'out',
    label: 'Closed',
    connection: node.payload.closedNodeId || uuid(),
  },
  {
    id: uuid(),
    type: 'out',
    label: 'Open',
    connection: node.payload.openNodeId || uuid(),
  },
];

export class OpeningHoursNode extends NodeTypeComponent<FlowNodeOpeningHoursT>() {
  static icon: IconNames = 'clock';
  static label = 'Opening hours';
  static helpText =
    'By setting your opening hours, you can decide what happens to incoming conversations based on whether your business is open or closed.';
  static color = flowNodeColor.purple;
  static height = FlowNode.dimensions.h + 16;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = createOpeningHoursModalContainer(false);
  static actionTrigger = { type: FlowNodeTypes.OPENING_HOURS };
  static updateConnection = (
    node: FlowNodeOpeningHoursT,
    port: InternalPortT,
    newConnection: string,
  ): FlowNodeOpeningHoursT => {
    const payload: FlowNodeOpeningHoursPayloadT = { ...node.payload };
    const entryName = port.label === 'Open' ? 'openNodeId' : 'closedNodeId';
    payload[entryName] = newConnection;

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const { node } = this.props;

    return (
      <FlowNode
        color={OpeningHoursNode.color}
        icon={OpeningHoursNode.icon}
        label={OpeningHoursNode.label}
        text={<OpeningHoursFromPayload node={node} />}
        onEdit={this.triggerEdit}
      />
    );
  }
}
